// Typed readers for the wizard's RFC 9457 problem responses (Module 02 §5, §9.1).
//
// The API merges its problem EXTENSIONS as top-level members of the problem
// document (platform/problem.filter.ts spreads `spec.extensions`), so a
// STALE_PAYLOAD body looks like
//   { type, title, status: 409, code: "STALE_PAYLOAD", server: {…}, request_id }
// and a go-live refusal like
//   { …, code: "SCORE_BELOW_THRESHOLD", score: 68, required: 85, gap: 17 }.
// `components["schemas"]["Problem"]` does not declare those members (they are
// per-code), so everything here re-reads them defensively from `unknown` — no
// casts that could lie, no `any`.
//
// Every reader is pure, so the shared primitives that render these
// (SaveContinueBar, GoLiveButton, the notice components) are unit-testable.

import type { components } from "@assessly/api-client";
import { RequestError } from "../../../lib/request";

type S = components["schemas"];

/** Wizard problem codes the UI branches on (§5). */
export const WIZARD_PROBLEM_CODES = {
  stalePayload: "STALE_PAYLOAD",
  alreadyLive: "ALREADY_LIVE",
  preconditionsUnmet: "PRECONDITIONS_UNMET",
  blockingItemsUnmet: "BLOCKING_ITEMS_UNMET",
  scoreBelowThreshold: "SCORE_BELOW_THRESHOLD",
  resourceBusy: "RESOURCE_BUSY",
  entityInUse: "ENTITY_IN_USE",
  duplicateCode: "DUPLICATE_CODE",
  blockingWaiveForbidden: "BLOCKING_WAIVE_FORBIDDEN",
  checkNotAvailable: "CHECK_NOT_AVAILABLE",
  itemNotManual: "ITEM_NOT_MANUAL",
  // UPPER_SNAKE per D-022 — this is the code the import service actually emits
  // (`import.service.ts` → "HAS_ERRORS"), and `isCode` is exact-match, so the
  // old lowercase spelling would have silently never matched.
  hasErrors: "HAS_ERRORS",
} as const;

function bag(error: unknown): Record<string, unknown> | undefined {
  if (!(error instanceof RequestError) || error.problem === undefined) return undefined;
  return error.problem;
}

function readString(source: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = source?.[key];
  return typeof value === "string" ? value : undefined;
}

function readNumber(source: Record<string, unknown> | undefined, key: string): number | undefined {
  const value = source?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readStringArray(source: Record<string, unknown> | undefined, key: string): string[] {
  const value = source?.[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function readNumberArray(source: Record<string, unknown> | undefined, key: string): number[] {
  const value = source?.[key];
  return Array.isArray(value)
    ? value.filter((v): v is number => typeof v === "number" && Number.isFinite(v))
    : [];
}

/** The problem `code` of a failed call, when the API sent one. */
export function problemCode(error: unknown): string | undefined {
  return error instanceof RequestError ? error.code : undefined;
}

/** HTTP status of a failed call (0 when the error is not a RequestError). */
export function problemStatus(error: unknown): number {
  return error instanceof RequestError ? error.httpStatus : 0;
}

export function isCode(error: unknown, code: string): boolean {
  return problemCode(error) === code;
}

/** 409 ALREADY_LIVE — the wizard is frozen; the screen must go read-only. */
export function isAlreadyLive(error: unknown): boolean {
  return isCode(error, WIZARD_PROBLEM_CODES.alreadyLive);
}

/** 503 RESOURCE_BUSY — the per-tenant wizard advisory lock is held; retry. */
export function isResourceBusy(error: unknown): boolean {
  return isCode(error, WIZARD_PROBLEM_CODES.resourceBusy);
}

/**
 * 409 STALE_PAYLOAD (§9.1 optimistic concurrency): somebody else saved this step
 * since we loaded it. The problem carries the CURRENT server step state in
 * `server` — the screen re-seeds its editor from that rather than clobbering.
 * Returns `undefined` when the error is a different failure.
 */
export function stalePayloadServerStep(error: unknown): S["OnboardingStepState"] | undefined {
  if (!isCode(error, WIZARD_PROBLEM_CODES.stalePayload)) return undefined;
  const server = bag(error)?.["server"];
  if (server === null || typeof server !== "object") return undefined;
  const candidate = server as Record<string, unknown>;
  // Shape-check the two fields the concurrency protocol actually needs before
  // handing it back as a step state (`updated_at` is the next If-Match token).
  if (typeof candidate["id"] !== "string" || typeof candidate["updated_at"] !== "string") {
    return undefined;
  }
  return server as S["OnboardingStepState"];
}

/** 422 PRECONDITIONS_UNMET, as raised by step completion and by go-live. */
export interface PreconditionFailure {
  /** Precondition keys, e.g. `vertical_pack_installed`, `all_steps_completed`. */
  readonly unmet: readonly string[];
  /** Step the completion was refused for (step-complete flavour). */
  readonly stepNo: number | undefined;
  /** Steps still incomplete (go-live flavour). */
  readonly incompleteSteps: readonly number[];
  /** The session status the server saw (go-live flavour). */
  readonly sessionStatus: string | undefined;
}

export function preconditionFailure(error: unknown): PreconditionFailure | undefined {
  if (!isCode(error, WIZARD_PROBLEM_CODES.preconditionsUnmet)) return undefined;
  const source = bag(error);
  return {
    unmet: readStringArray(source, "unmet"),
    stepNo: readNumber(source, "step_no"),
    incompleteSteps: readNumberArray(source, "incomplete_steps"),
    sessionStatus: readString(source, "session_status"),
  };
}

/** Why go-live was refused (§3.6). One of the three 422s, or an ALREADY_LIVE. */
export interface GoLiveFailure {
  readonly code: string;
  readonly unmet: readonly string[];
  readonly incompleteSteps: readonly number[];
  readonly blockingUnmet: readonly string[];
  readonly score: number | undefined;
  readonly required: number | undefined;
  readonly gap: number | undefined;
}

export function goLiveFailure(error: unknown): GoLiveFailure | undefined {
  const code = problemCode(error);
  if (
    code !== WIZARD_PROBLEM_CODES.preconditionsUnmet &&
    code !== WIZARD_PROBLEM_CODES.blockingItemsUnmet &&
    code !== WIZARD_PROBLEM_CODES.scoreBelowThreshold &&
    code !== WIZARD_PROBLEM_CODES.alreadyLive
  ) {
    return undefined;
  }
  const source = bag(error);
  return {
    code,
    unmet: readStringArray(source, "unmet"),
    incompleteSteps: readNumberArray(source, "incomplete_steps"),
    blockingUnmet: readStringArray(source, "blocking_unmet"),
    score: readNumber(source, "score"),
    required: readNumber(source, "required"),
    gap: readNumber(source, "gap"),
  };
}

/** 409 ENTITY_IN_USE from the plant-map commit (§3.4): the refused nodes. */
export interface RefusedNode {
  readonly code: string;
  readonly path: string;
  readonly reason: string;
}

export function refusedNodes(error: unknown): readonly RefusedNode[] {
  if (!isCode(error, WIZARD_PROBLEM_CODES.entityInUse)) return [];
  const refused = bag(error)?.["refused"];
  if (!Array.isArray(refused)) return [];
  return refused.flatMap((entry): RefusedNode[] => {
    if (entry === null || typeof entry !== "object") return [];
    const row = entry as Record<string, unknown>;
    const code = typeof row["code"] === "string" ? row["code"] : "";
    const path = typeof row["path"] === "string" ? row["path"] : "";
    const reason = typeof row["reason"] === "string" ? row["reason"] : "";
    return [{ code, path, reason }];
  });
}

/**
 * The i18n key for a precondition / blocking-item key, with the raw key as the
 * i18next `defaultValue` so an unmapped key degrades to something readable
 * instead of rendering `onboarding.precondition.foo` at a user.
 */
export function preconditionLabelKey(key: string): string {
  return `onboarding.precondition.${key}`;
}

/** The i18n key for a checklist item key (§3.6 closed catalog of 19). */
export function checklistItemLabelKey(itemKey: string): string {
  return `onboarding.checklist.items.${itemKey}.title`;
}
